import Sheet from '../../shared/components/Sheet';
import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import './DailyClosing.css';

interface CashCounts {
  bills_100: number;
  bills_50: number;
  bills_20: number;
  bills_10: number;
  bills_5: number;
  coins_toonies: number;
  coins_loonies: number;
  coins_quarters: number;
  coins_dimes: number;
  coins_nickels: number;
  coins_pennies: number;
}

const DailyClosing: React.FC = () => {
  const navigate = useNavigate();
  const { sessionToken } = useAuth();
  const { showToast, fetchNotifications } = useNotification();

  // Toronto local date, formatted YYYY-MM-DD.
  const getTorontoDate = (d: Date) =>
    d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });

  const getTodayDate = () => getTorontoDate(new Date());

  // Default the form to the *business day* being closed: if it's currently
  // between midnight and 5am in Toronto, the close is almost certainly for
  // yesterday's business (cafe open till midnight Fri/Sat). Filing for "today"
  // also queries Clover for a day that has barely started, returning $0 cash
  // sales and creating phantom mismatches.
  const getDefaultBusinessDate = () => {
    const now = new Date();
    const torontoHour = parseInt(
      now.toLocaleString('en-US', { timeZone: 'America/Toronto', hour: 'numeric', hour12: false }),
      10,
    );
    if (torontoHour < 5) {
      return getTorontoDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
    }
    return getTorontoDate(now);
  };

  const [transactionDate, setTransactionDate] = useState(getDefaultBusinessDate());
  const [counts, setCounts] = useState<CashCounts>({
    bills_100: 0,
    bills_50: 0,
    bills_20: 0,
    bills_10: 0,
    bills_5: 0,
    coins_toonies: 0,
    coins_loonies: 0,
    coins_quarters: 0,
    coins_dimes: 0,
    coins_nickels: 0,
    coins_pennies: 0
  });

  const [totalAmount, setTotalAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingMismatch, setPendingMismatch] = useState<any | null>(null);

  const [saved, setSaved] = useState(false);
  useEditProtection(!saved && Object.values(counts).some(n => Number(n) > 0));
  const denominations = useMemo(() => [
    { key: 'bills_100', label: '$100 Bills', value: 100, type: 'bill' },
    { key: 'bills_50', label: '$50 Bills', value: 50, type: 'bill' },
    { key: 'bills_20', label: '$20 Bills', value: 20, type: 'bill' },
    { key: 'bills_10', label: '$10 Bills', value: 10, type: 'bill' },
    { key: 'bills_5', label: '$5 Bills', value: 5, type: 'bill' },
    { key: 'coins_toonies', label: 'Toonies ($2)', value: 2, type: 'coin' },
    { key: 'coins_loonies', label: 'Loonies ($1)', value: 1, type: 'coin' },
    { key: 'coins_quarters', label: 'Quarters ($0.25)', value: 0.25, type: 'coin' },
    { key: 'coins_dimes', label: 'Dimes ($0.10)', value: 0.10, type: 'coin' },
    { key: 'coins_nickels', label: 'Nickels ($0.05)', value: 0.05, type: 'coin' },
    { key: 'coins_pennies', label: 'Pennies ($0.01)', value: 0.01, type: 'coin' }
  ], []);

  useEffect(() => {
    const total = denominations.reduce((sum, denom) => {
      const count = counts[denom.key as keyof CashCounts];
      return sum + (count * denom.value);
    }, 0);
    setTotalAmount(Math.round(total * 100) / 100);
  }, [counts, denominations]);

  const updateCount = (key: keyof CashCounts, value: number) => {
    setCounts(prev => ({
      ...prev,
      [key]: Math.max(0, value)
    }));
  };

  // Two-step submit: first dry-run to check reconciliation, then either save
  // straight through (match / skipped) or pop a confirm modal (mismatch) so
  // the staff can recount before persisting.
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const previewRes = await fetch('/api/daily-closing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({ transaction_date: transactionDate, dry_run: true, ...counts }),
        cache: 'no-store',
      });
      const previewData = await previewRes.json().catch(() => ({}));
      if (!previewRes.ok) throw new Error(previewData.error || `Server error: ${previewRes.status}`);

      const recon = previewData.reconciliation;
      setPendingMismatch(recon || { skipped: true, reason: 'Reconciliation unavailable' });
      setIsSubmitting(false);
    } catch (error: any) {
      console.error('Error checking daily closing:', error);
      showToast({ type: 'error', title: 'Error', message: `Failed to verify cash count: ${error.message}` });
      setIsSubmitting(false);
    }
  };

  const finalize = async (afterMismatchConfirm: boolean) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/daily-closing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({ transaction_date: transactionDate, ...counts }),
        cache: 'no-store',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Server error: ${response.status}`);

      setPendingMismatch(null);
      setSaved(true);
      fetchNotifications();
      setTimeout(() => navigate('/cash', {
        state: {
          toast: afterMismatchConfirm
            ? {
                type: 'info',
                title: 'Closing saved — flagged for review',
                message: `Recorded $${data.totalAmount.toFixed(2)}. Owners notified.`,
              }
            : {
                type: 'success',
                title: 'Daily Closing Complete',
                message: `Total cash: $${data.totalAmount.toFixed(2)}`,
              },
        },
      }), 0);
    } catch (error: any) {
      console.error('Error submitting daily closing:', error);
      showToast({ type: 'error', title: 'Error', message: `Failed to submit daily closing: ${error.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="daily-closing">
      <div className="header">
        <button onClick={() => navigate('/cash/new')} className="back-btn">← Back</button>
        <h2>Daily Cash Count</h2>
      </div>

      <div className="date-section">
        <label htmlFor="transaction-date">Transaction Date:</label>
        <input
          id="transaction-date"
          type="date"
          value={transactionDate}
          onChange={(e) => setTransactionDate(e.target.value)}
          max={getTodayDate()}
          className="date-input"
        />
      </div>

      <div className="total-display cs-sticky">
        <h3>Total Cash: ${totalAmount.toFixed(2)}</h3>
      </div>

      <div className="denomination-sections">
        <div className="section">
          <h4>💵 Bills</h4>
          <div className="denomination-grid">
            {denominations
              .filter(d => d.type === 'bill')
              .map(denom => (
                <div key={denom.key} className="denomination-item">
                  <label>{denom.label}</label>
                  <div className="counter">
                    <button
                      type="button"
                      onClick={() => updateCount(denom.key as keyof CashCounts, counts[denom.key as keyof CashCounts] - 1)}
                      className="counter-btn"
                    >
                      −
                    </button>
                    <input
                      aria-label={`Count of ${denom.label}`} inputMode="numeric" enterKeyHint="next" type="number"
                      min="0"
                      value={counts[denom.key as keyof CashCounts] || ''}
                      placeholder="0"
                      onChange={(e) => updateCount(denom.key as keyof CashCounts, parseInt(e.target.value) || 0)}
                      onFocus={(e) => e.target.select()}
                      className="counter-input"
                    />
                    <button
                      type="button"
                      onClick={() => updateCount(denom.key as keyof CashCounts, counts[denom.key as keyof CashCounts] + 1)}
                      className="counter-btn"
                    >
                      +
                    </button>
                  </div>
                  <div className="subtotal">
                    ${(counts[denom.key as keyof CashCounts] * denom.value).toFixed(2)}
                  </div>
                </div>
              ))
            }
          </div>
        </div>

        <div className="section">
          <h4>🪙 Coins</h4>
          <div className="denomination-grid">
            {denominations
              .filter(d => d.type === 'coin')
              .map(denom => (
                <div key={denom.key} className="denomination-item">
                  <label>{denom.label}</label>
                  <div className="counter">
                    <button
                      type="button"
                      onClick={() => updateCount(denom.key as keyof CashCounts, counts[denom.key as keyof CashCounts] - 1)}
                      className="counter-btn"
                    >
                      −
                    </button>
                    <input
                      aria-label={`Count of ${denom.label}`} inputMode="numeric" enterKeyHint="next" type="number"
                      min="0"
                      value={counts[denom.key as keyof CashCounts] || ''}
                      placeholder="0"
                      onChange={(e) => updateCount(denom.key as keyof CashCounts, parseInt(e.target.value) || 0)}
                      onFocus={(e) => e.target.select()}
                      className="counter-input"
                    />
                    <button
                      type="button"
                      onClick={() => updateCount(denom.key as keyof CashCounts, counts[denom.key as keyof CashCounts] + 1)}
                      className="counter-btn"
                    >
                      +
                    </button>
                  </div>
                  <div className="subtotal">
                    ${(counts[denom.key as keyof CashCounts] * denom.value).toFixed(2)}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div className="submit-section">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="submit-btn"
        >
          {isSubmitting ? 'Submitting...' : 'Review daily closing'}
        </button>
      </div>

      {pendingMismatch && (
        <Sheet title="Review daily closing" onClose={() => setPendingMismatch(null)} busy={isSubmitting}>
          <p>{transactionDate} · Counted cash: <strong>${totalAmount.toFixed(2)}</strong></p>
          {pendingMismatch.skipped ? <p>Expected balance unavailable: {pendingMismatch.reason}. You can save this count without reconciliation.</p> : <>
            <h3 style={{ marginTop: 0, color: '#d32f2f' }}>{pendingMismatch.ok ? 'Cash count matches' : 'Cash count differs from Clover'}</h3>
            <p style={{ color: '#555' }}>
              {pendingMismatch.ok ? 'Review and confirm the counted cash.' : 'Recount or save the difference for the owner to review.'}
            </p>
            <div style={{ background: '#fff5f5', padding: '0.75rem 1rem', borderLeft: '4px solid #d32f2f', margin: '1rem 0' }}>
              <div>Previous close: ${pendingMismatch.details.previous_close.toFixed(2)}</div>
              <div>Clover cash sales: +${pendingMismatch.details.clover_cash_sales.toFixed(2)} ({pendingMismatch.details.clover_payment_count} payments)</div>
              <div>Cash added to till: +${(pendingMismatch.details.cash_additions || 0).toFixed(2)}</div>
              <div>Withdrawals from till: -${pendingMismatch.details.withdrawals.toFixed(2)}</div>
              <div>Spending from till: -${pendingMismatch.details.spending.toFixed(2)}</div>
              <div style={{ marginTop: 6 }}><strong>Expected: ${pendingMismatch.expected.toFixed(2)}</strong></div>
              <div><strong>You entered: ${pendingMismatch.entered.toFixed(2)}</strong></div>
              <div style={{ color: '#d32f2f', marginTop: 6 }}>
                <strong>
                  Discrepancy: {pendingMismatch.discrepancy >= 0 ? '+' : ''}${pendingMismatch.discrepancy.toFixed(2)}
                </strong>
              </div>
            </div>
            </>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setPendingMismatch(null)} disabled={isSubmitting}
                style={{ padding: '0.5rem 1rem', borderRadius: 4, border: '1px solid #5b2828', background: '#fff', color: '#5b2828', cursor: 'pointer' }}
              >
                Recount
              </button>
              <button
                type="button"
                onClick={() => finalize(pendingMismatch.ok === false)}
                disabled={isSubmitting}
                style={{ padding: '0.5rem 1rem', borderRadius: 4, border: 'none', background: '#d32f2f', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                {isSubmitting ? 'Saving…' : pendingMismatch.ok === false ? 'Save and flag difference' : 'Confirm closing'}
              </button>
            </div>
        </Sheet>
      )}
    </div>
  );
};

export default DailyClosing;
